import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { Category, Listing, RootCategory } from 'src/api/v1-api';
import { apiRequest } from 'src/utils/fetchUtils';
import { useAuth } from 'src/utils/auth';
import ListingCarousel from 'src/components/atoms/listing-carousel';

interface FeaturedListingsBarProps {
  requiresAuth?: boolean;
  actionButtons?: string;
  category?: Category | RootCategory | null;
}

const shuffleArray = (array: Listing[]) => {
  const shuffled = array
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);

  return shuffled;
};

const FeaturedListingsBar: React.FC<FeaturedListingsBarProps> = ({
  requiresAuth,
  actionButtons = '',
  category,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [listings, setListings] = useState<Listing[]>([]);
  const [hasEnoughListings, setHasEnoughListings] = useState<boolean>(true);
  const { getToken } = useAuth();
  const fetchInProgress = useRef<boolean>(false);

  const shuffle_featured_listings = category?.functional?.shuffle_featured_listings ?? false;
  const inventoryDisplay = category?.functional?.inventory_display;
  let columns = 4;
  if (inventoryDisplay === '3_3_grid') {
    columns = 3;
  } else if (inventoryDisplay === '2_4_grid') {
    columns = 2;
  }

  const fetchListings = useCallback(async () => {
    // Guard against concurrent calls
    if (fetchInProgress.current) return;

    fetchInProgress.current = true;
    setLoading(true);
    try {
      let token = null;
      if (requiresAuth) {
        token = await getToken();
      }

      const fetchUrl = `${process.env.NEXT_PUBLIC_API_HOST}/listings/category/${category?.id}/?is_featured=true`;
      const response = await apiRequest('GET', fetchUrl, token);
      let data = await response.results;
      if (data.length > 2 && shuffle_featured_listings) {
        data = shuffleArray(data);
      }
      setListings(data);

      // Check if we have enough listings to display the bar
      const meetsMinimum = data.length >= 2;
      setHasEnoughListings(meetsMinimum);
    } catch (error) {
      console.error(error);
      setHasEnoughListings(false);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [getToken, requiresAuth, category?.id, shuffle_featured_listings]);

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.id, shuffle_featured_listings, requiresAuth]);

  // Don't render anything if we've loaded and don't have enough listings
  if (!loading && !hasEnoughListings) {
    return null;
  }

  return (
    <Flex direction="column" position="relative">
      <ListingCarousel
        listings={listings}
        columns={columns}
        loading={loading}
        isFeatured={true}
        actionButtons={actionButtons}
        category={category}
      />
    </Flex>
  );
};

export default FeaturedListingsBar;
